<template>
	<div>
		<SubtitleText> {{ t('code') }} </SubtitleText>
		<div class="mt-2 flex w-full gap-2">
			<div
				class="border-separator-secondary shadow-input flex h-10 min-w-0 flex-1 items-center rounded-[10px] border bg-black pr-1.5 pl-3 text-base"
			>
				<CheckIcon
					class="text-green h-5 w-5 shrink-0"
					v-if="user.activatedCode && !activateError"
				/>
				<CloseSmIcon v-if="activateError" class="text-red h-5 w-5 shrink-0" />
				<TicketIcon
					class="text-label-secondary h-5 w-5 shrink-0"
					v-if="!user.activatedCode && !activateError"
				/>
				<BaseInput
					ref="codeInput"
					id="affiliateCode"
					type="text"
					class="min-w-0 flex-1 py-2 pr-3 pl-1.5 uppercase placeholder:normal-case disabled:opacity-60"
					:placeholder="t('enterCode')"
					v-model="code"
					:disabled="!!user.activatedCode"
				/>
				<PriceDiscount
					v-if="user.activatedCode && user.depositBonus"
					:bonus="user.depositBonus"
				/>
				<div
					class="bg-fill-secondary ml-1.5 shrink-0 rounded-full p-1"
					v-hover="{ active: '*:scale-95', impact: 'soft' }"
					v-if="user.activatedCode && user.activatedCodeRemovable"
					@click="resetCode()"
				>
					<CloseSmIcon class="text-label-tertiary h-5 w-5" v-if="!resetting" />
					<LoaderIcon v-else class="text-label-tertiary h-5 w-5" />
				</div>
			</div>
			<BaseButton
				size="md"
				color="primary"
				class="shrink-0"
				v-if="!user.activatedCode"
				@click="activate()"
				:loading="activating"
			>
				{{ t('apply') }}
			</BaseButton>
		</div>
		<div
			class="text-label-tertiary mt-3 flex items-center gap-2 text-sm"
			@click="fillCode('RAGE')"
		>
			<NoteOutlineIcon class="h-5 w-5 shrink-0" />
			<div>
				{{
					user.activatedCode ? t('daily', [user.dailyCoins / 100]) : t('rage')
				}}
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { useUser } from '@/stores/user'
import SubtitleText from './SubtitleText.vue'
import BaseButton from './BaseButton.vue'
import BaseInput from './BaseInput.vue'
import PriceDiscount from './PriceDiscount.vue'
import { useI18n } from 'vue-i18n'
import CloseSmIcon from '@/assets/icons/close-sm.svg'
import TicketIcon from '@/assets/icons/ticket.svg'
import CheckIcon from '@/assets/icons/check.svg'
import LoaderIcon from '@/assets/icons/loader.svg'
import NoteOutlineIcon from '@/assets/icons/note-outline.svg'
import { nextTick, ref, watch } from 'vue'

const { t } = useI18n({
	messages: {
		en: {
			code: 'Affiliate Code',
			enterCode: 'Enter Code...',
			apply: 'apply',
			daily: 'Up to {0} bonus coins daily',
			rage: `Don't have a code? Use "RAGE"`
		},
		ru: {
			code: 'Промокод',
			enterCode: 'Введи код...',
			apply: 'применить',
			daily: 'До {0} бонусных монет ежедневно',
			rage: 'Нет кода? Используй "RAGE"'
		}
	}
})

const user = useUser()

const code = ref('')
const oldCode = ref('')
const activateError = ref(false)
const resetting = ref(false)

watch(code, () => {
	activateError.value = false
})

watch(
	() => user.activatedCode,
	() => {
		code.value = user.activatedCode
	},
	{ immediate: true }
)

const codeInput = ref<InstanceType<any>>()

const resetToOldCode = () => {
	if (!oldCode.value) {
		return
	}

	code.value = oldCode.value
	user.setActivatedCode(code.value)
	oldCode.value = ''
}

const resetCode = async () => {
	oldCode.value = code.value
	code.value = ''
	user.setActivatedCode('')

	await nextTick()

	if (!codeInput.value || !codeInput.value.focus) {
		return
	}

	codeInput.value.focus()
}

const fillCode = (value: string) => {
	if (user.activatedCode) {
		return
	}

	code.value = value
}

const activating = ref(false)
const activate = async () => {
	if (user.activatedCode) {
		return
	}
	if (!code.value) {
		return
	}

	activating.value = true

	const result = await user.activateCode(code.value)

	activating.value = false

	if (!result) {
		activateError.value = true
	} else {
		oldCode.value = ''
	}
}

defineExpose({
	resetToOldCode
})
</script>
